import type { CatalogFacet, PreviewFilter, VoiceStatusFilter } from './geminiCatalogFilters.ts';
import { catalogAccentFilters } from './catalogAccents.ts';

type ViewStorage = Pick<Storage, 'getItem' | 'setItem'>;
export type VoiceReviewView = {
  query: string; status: VoiceStatusFilter; preview: PreviewFilter;
  filters: Partial<Record<CatalogFacet, string>>; accentFilter: string;
  page: number; currentVoiceId: string | null;
};
const key = (userId: string, view: string) => `tableread.admin.review.v1:${userId}:${view}`;
const emptyView = (): VoiceReviewView => ({ query: '', status: 'all', preview: 'all', filters: {}, accentFilter: '', page: 0, currentVoiceId: null });
const store = (storage?: ViewStorage) => storage ?? (typeof window !== 'undefined' ? window.sessionStorage : null);

export function readAdminTab<T extends string>(userId: string | undefined, tabs: readonly T[], storage?: ViewStorage): T | null {
  if (!userId) return null;
  try { const value = store(storage)?.getItem(key(userId, 'tab')); return tabs.includes(value as T) ? value as T : null; }
  catch { return null; }
}
export function writeAdminTab(userId: string | undefined, tab: string, storage?: ViewStorage): void {
  if (!userId) return;
  try { if (tab.length <= 100) store(storage)?.setItem(key(userId, 'tab'), tab); } catch { /* Browser-only convenience, not an auth decision. */ }
}
function validatedView(value: unknown): VoiceReviewView | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const view = value as Record<string, unknown>, filters = view.filters as Record<string, unknown> | null;
  if (typeof view.query !== 'string' || view.query.length > 512 || !['all', 'active', 'inactive'].includes(String(view.status)) ||
    !['all', 'available', 'missing'].includes(String(view.preview)) || typeof view.accentFilter !== 'string' || view.accentFilter.length > 512 ||
    !Number.isSafeInteger(view.page) || (view.page as number) < 0 || (view.page as number) > 100000 ||
    (view.currentVoiceId !== null && (typeof view.currentVoiceId !== 'string' || view.currentVoiceId.length > 128 || /[<>\u0000-\u001f]/.test(view.currentVoiceId))) ||
    !filters || typeof filters !== 'object' || Array.isArray(filters)) return null;
  try { catalogAccentFilters(view.accentFilter); } catch { return null; }
  const allowedFilters: VoiceReviewView['filters'] = {};
  for (const [facet, values] of [['gender', ['', 'Not specified', 'Female', 'Male', 'Neutral']], ['pitch', ['', 'Not specified', 'Low', 'Medium', 'High']]] as const) {
    if (filters[facet] === undefined) continue;
    if (typeof filters[facet] !== 'string' || !(values as readonly string[]).includes(filters[facet] as string)) return null;
    allowedFilters[facet] = filters[facet] as string;
  }
  // Whitelist UI coordinates only. Never persist credentials, signed sample
  // URLs, actor metadata, edit drafts, or the fact that audio was playing.
  return { query: view.query, status: view.status as VoiceStatusFilter, preview: view.preview as PreviewFilter,
    filters: allowedFilters, accentFilter: view.accentFilter, page: view.page as number, currentVoiceId: view.currentVoiceId as string | null };
}
export function readVoiceReviewView(userId?: string, storage?: ViewStorage): VoiceReviewView {
  if (!userId) return emptyView();
  try {
    const value = store(storage)?.getItem(key(userId, 'voices'));
    return value && value.length <= 8192 ? validatedView(JSON.parse(value)) ?? emptyView() : emptyView();
  } catch { return emptyView(); }
}
export function writeVoiceReviewView(userId: string | undefined, value: VoiceReviewView, storage?: ViewStorage): void {
  if (!userId) return;
  try { const safe = validatedView(value); if (safe) store(storage)?.setItem(key(userId, 'voices'), JSON.stringify(safe)); } catch { /* Blocked storage cannot break playback. */ }
}
